/**
 * Calculates totals for a task, including all subtasks and direct materials.
 *
 * @param {Object} params
 * @param {Array} params.material - Array of material items directly on the task.
 * @param {Array} params.subtasks - Array of subtask objects (each with their own materials, labor, etc).
 * @param {boolean} [params.overrideProfitMargin=false] - If true, overrides profit margin for locked sale amounts.
 * @param {string} params.profitMarginType - Type of profit margin: 'PERCENTAGE' or 'FIXED'.
 * @param {number|string} params.profitMargin - The profit margin value to apply if locked.
 * @param {boolean} [params.isForm=false] - If true, expects form-style data structure for labor items.
 *
 * @returns {Object} total - Totals for material, subcontractor, labor, and overall profit, subtotal, forecast, and total.
 *
 * Use case: Used to calculate and summarize all cost, sale, and profit data for a task, including all subtasks and direct materials.
 */

import { Decimal, toDecimal, roundValue } from '../decimal-utils';
const { default: idx } = require('idx');
const { subTaskCalculation } = require('./calculate-sub-task');
const { isString } = require('lodash');

// Helper function to convert object with numeric values to Decimal values
const toDecimalObject = (obj) => {
  if (!obj) return obj;
  const result = {};
  for (const key in obj) {
    if (obj[key] && typeof obj[key] === 'object' && !(obj[key] instanceof Decimal)) {
      result[key] = toDecimalObject(obj[key]);
    } else {
      result[key] = toDecimal(obj[key] || 0);
    }
  }
  return result;
};

// Helper function to convert Decimal object back to regular numbers with rounding
const toRoundedObject = (decimalObj) => {
  if (!decimalObj) return decimalObj;
  if (decimalObj instanceof Decimal) return roundValue(decimalObj, 2);

  const result = Array.isArray(decimalObj) ? [] : {};
  for (const key in decimalObj) {
    const value = decimalObj[key];
    if (value instanceof Decimal) {
      result[key] = roundValue(value, 2);
    } else if (value && typeof value === 'object') {
      result[key] = toRoundedObject(value);
    } else {
      result[key] = value;
    }
  }
  return result;
};

/**
 * Recursively calculates and aggregates cost, sale, and profit for all tasks and subtasks within nested stages.
 * Returns an object keyed by task ID with their respective totals.
 */
export const recursiveStageCalculation = ({ stages = [], isForm = false }) => {
  const calculations = { task: {} };

  // Helper to process a single stage and its children
  const processStage = (stage) => {
    // Process all tasks in this stage
    if (stage.tasks && stage.tasks.length > 0) {
      stage.tasks.forEach((task) => {
        let taskTotal = {
          material: { cost: new Decimal(0), sale: new Decimal(0), profit: new Decimal(0) },
          subcontractor: { cost: new Decimal(0), sale: new Decimal(0), profit: new Decimal(0) },
          labor: { cost: new Decimal(0), sale: new Decimal(0), profit: new Decimal(0) },
          total_profit: new Decimal(0),
          subtotal: new Decimal(0),
          forecast: new Decimal(0),
          forecastHours: new Decimal(0),
          total: new Decimal(0),
        };
        // Aggregate all subtasks for this task
        const subTasks = task.subTasks || [];
        subTasks.forEach((subTask) => {
          const subtaskTotal = subTaskCalculation({
            subtask: subTask,
            profitMargin: task.profitMargin,
            profitMarginType: task.profitMarginType,
            isForm,
          });
          // Convert subtask totals to Decimal for precise arithmetic
          const subTaskDecimals = toDecimalObject(subtaskTotal);

          // Accumulate values using Decimal arithmetic (no rounding yet)
          taskTotal.material.cost = taskTotal.material.cost.plus(subTaskDecimals.material.cost);
          taskTotal.material.sale = taskTotal.material.sale.plus(subTaskDecimals.material.sale);
          taskTotal.material.profit = taskTotal.material.profit.plus(subTaskDecimals.material.profit);

          taskTotal.subcontractor.cost = taskTotal.subcontractor.cost.plus(subTaskDecimals.subcontractor.cost);
          taskTotal.subcontractor.sale = taskTotal.subcontractor.sale.plus(subTaskDecimals.subcontractor.sale);
          taskTotal.subcontractor.profit = taskTotal.subcontractor.profit.plus(subTaskDecimals.subcontractor.profit);

          taskTotal.labor.cost = taskTotal.labor.cost.plus(subTaskDecimals.labor.cost);
          taskTotal.labor.sale = taskTotal.labor.sale.plus(subTaskDecimals.labor.sale);
          taskTotal.labor.profit = taskTotal.labor.profit.plus(subTaskDecimals.labor.profit);

          taskTotal.total_profit = taskTotal.total_profit.plus(subTaskDecimals.total_profit);
          taskTotal.subtotal = taskTotal.subtotal.plus(subTaskDecimals.subtotal);
          taskTotal.forecast = taskTotal.forecast.plus(subTaskDecimals.forecast);
          taskTotal.forecastHours = taskTotal.forecastHours.plus(subTaskDecimals.forecastHours);
          taskTotal.total = taskTotal.total.plus(subTaskDecimals.total);
        });
        calculations.task[task.id] = taskTotal;
      });
    }
    // Recursively process any nested stages
    if (stage.stages && Array.isArray(stage.stages)) {
      stage.stages.forEach((nestedStage) => processStage(nestedStage));
    }
  };

  stages.forEach((stage) => processStage(stage));
  return calculations;
};

export const stageCalculation = ({
  project = {},
  stages = [],
  isForm = false,
}) => {
  // Initialize all numeric values as Decimal for precise arithmetic
  let total = {
    stage: {},
    task: {},
    subtask: {},
    material: {
      cost: new Decimal(0),
      sale: new Decimal(0),
      profit: new Decimal(0)
    },
    subcontractor: {
      cost: new Decimal(0),
      sale: new Decimal(0),
      profit: new Decimal(0)
    },
    labor: {
      cost: new Decimal(0),
      sale: new Decimal(0),
      profit: new Decimal(0)
    },
    total_profit: new Decimal(0),
    forecast_profit: new Decimal(0),
    revised_cost: new Decimal(0),
    subtotal: new Decimal(0),
    forecast: new Decimal(0),
    forecastHours: new Decimal(0),
    total: new Decimal(0),
  };

  try {
    // Use project.stages if available, otherwise use provided stages
    let _stages = project.stages || stages || [];
    for (const stage of _stages) {
      // Initialize Decimal values for stage totals
      let stage_total = {
        material: {
          cost: new Decimal(0),
          sale: new Decimal(0),
          profit: new Decimal(0)
        },
        subcontractor: {
          cost: new Decimal(0),
          sale: new Decimal(0),
          profit: new Decimal(0)
        },
        labor: {
          cost: new Decimal(0),
          sale: new Decimal(0),
          profit: new Decimal(0)
        },
        total_profit: new Decimal(0),
        subtotal: new Decimal(0),
        forecast: new Decimal(0),
        forecastHours: new Decimal(0),
        total: new Decimal(0),
      };
      let tasks = stage.tasks || [];
      for (const task of tasks) {
        // Initialize Decimal values for task totals
        let task_total = {
          material: {
            cost: new Decimal(0),
            sale: new Decimal(0),
            profit: new Decimal(0)
          },
          subcontractor: {
            cost: new Decimal(0),
            sale: new Decimal(0),
            profit: new Decimal(0)
          },
          labor: {
            cost: new Decimal(0),
            sale: new Decimal(0),
            profit: new Decimal(0)
          },
          total_profit: new Decimal(0),
          subtotal: new Decimal(0),
          forecast: new Decimal(0),
          forecastHours: new Decimal(0),
          total: new Decimal(0),
        };
        let subTasks = task.subTasks || [];
        for (const subTask of subTasks) {
          // Calculate totals for this subtask
          let subtask_total = subTaskCalculation({
            subtask: subTask,
            profitMargin: idx(task, (_) => _.profitMargin),
            profitMarginType: idx(task, (_) => _.profitMarginType),
            isForm,
          });
          // Convert subtask totals to Decimal for precise arithmetic
          const subTaskDecimals = toDecimalObject(subtask_total);

          // Accumulate values using Decimal arithmetic (no rounding yet)
          task_total.material.cost = task_total.material.cost.plus(subTaskDecimals.material.cost);
          task_total.material.sale = task_total.material.sale.plus(subTaskDecimals.material.sale);
          task_total.material.profit = task_total.material.profit.plus(subTaskDecimals.material.profit);

          task_total.subcontractor.cost = task_total.subcontractor.cost.plus(subTaskDecimals.subcontractor.cost);
          task_total.subcontractor.sale = task_total.subcontractor.sale.plus(subTaskDecimals.subcontractor.sale);
          task_total.subcontractor.profit = task_total.subcontractor.profit.plus(subTaskDecimals.subcontractor.profit);

          task_total.labor.cost = task_total.labor.cost.plus(subTaskDecimals.labor.cost);
          task_total.labor.sale = task_total.labor.sale.plus(subTaskDecimals.labor.sale);
          task_total.labor.profit = task_total.labor.profit.plus(subTaskDecimals.labor.profit);

          task_total.total_profit = task_total.total_profit.plus(subTaskDecimals.total_profit);
          task_total.subtotal = task_total.subtotal.plus(subTaskDecimals.subtotal);
          task_total.forecast = task_total.forecast.plus(subTaskDecimals.forecast);
          task_total.forecastHours = task_total.forecastHours.plus(subTaskDecimals.forecastHours);
          task_total.total = task_total.total.plus(subTaskDecimals.total);

          // Store subtask totals by subtask id for reference
          total.subtask[subTask.id] = subtask_total;
        }
        // Aggregate direct materials (not in subtasks) into the task total
        let materials = task.material || [];
        materials = materials
          .map((material, i) => {
            if (!material) return undefined;
            // Parse material if it's a string
            let _material = isString(material)
              ? JSON.parse(material)
              : material;
            // If sale amount is locked, recalculate using profit margin
            if (_material['saleAmountLock'] === true) {
              const cost = toDecimal(_material['cost'] || 0);
              const profitMargin = toDecimal(idx(task, (_) => _.profitMargin) || 0);

              if (idx(task, (_) => _.profitMarginType) === 'PERCENTAGE') {
                // Calculate sale amount with percentage margin: cost * (1 + profitMargin/100)
                _material['saleAmount'] = roundValue(
                  cost.times(profitMargin.dividedBy(100).plus(1)),
                  2
                );
              } else if (idx(task, (_) => _.profitMarginType) === 'FIXED') {
                // Calculate sale amount with fixed margin: cost + profitMargin
                _material['saleAmount'] = roundValue(
                  cost.plus(profitMargin),
                  2
                );
              }
            }
            // Calculate cost and sale for this material using Decimal
            const qty = toDecimal(_material['qty'] || 1);
            const cost = toDecimal(_material['cost'] || 0).times(qty);
            const sale = toDecimal(_material['saleAmount'] || 0).times(qty);

            task_total.material.cost = task_total.material.cost.plus(cost);
            task_total.material.sale = task_total.material.sale.plus(sale);
            task_total.subtotal = task_total.subtotal.plus(cost);
            return JSON.stringify(_material);
          })
          .filter((item) => item);
        // Add this task's totals to the stage total using Decimal arithmetic
        stage_total.material.cost = stage_total.material.cost.plus(task_total.material.cost);
        stage_total.material.sale = stage_total.material.sale.plus(task_total.material.sale);
        stage_total.material.profit = stage_total.material.profit.plus(task_total.material.profit);

        stage_total.subcontractor.cost = stage_total.subcontractor.cost.plus(task_total.subcontractor.cost);
        stage_total.subcontractor.sale = stage_total.subcontractor.sale.plus(task_total.subcontractor.sale);
        stage_total.subcontractor.profit = stage_total.subcontractor.profit.plus(task_total.subcontractor.profit);

        stage_total.labor.cost = stage_total.labor.cost.plus(task_total.labor.cost);
        stage_total.labor.sale = stage_total.labor.sale.plus(task_total.labor.sale);
        stage_total.labor.profit = stage_total.labor.profit.plus(task_total.labor.profit);

        stage_total.total_profit = stage_total.total_profit.plus(task_total.total_profit);
        stage_total.subtotal = stage_total.subtotal.plus(task_total.subtotal);
        stage_total.forecast = stage_total.forecast.plus(task_total.forecast);
        stage_total.forecastHours = stage_total.forecastHours.plus(task_total.forecastHours);
        stage_total.total = stage_total.total.plus(task_total.total);

        // Calculate forecast profit and revised cost using Decimal arithmetic
        total.forecast_profit = total.forecast_profit.plus(
          toDecimal(task.materialForecastCost || 0)
            .plus(toDecimal(task.subContractorForecastCost || 0))
            .plus(toDecimal(task.laborForecastCost || 0))
        );

        total.revised_cost = total.revised_cost.plus(
          task_total.material.sale
            .plus(task_total.subcontractor.sale)
            .plus(task_total.labor.sale)
        );

        // Store task totals by task id for reference
        total.task[task.id] = task_total;
      }
      // Accumulate stage totals to project total using Decimal arithmetic
      total.material.cost = total.material.cost.plus(stage_total.material.cost);
      total.material.sale = total.material.sale.plus(stage_total.material.sale);
      total.material.profit = total.material.profit.plus(stage_total.material.profit);

      total.subcontractor.cost = total.subcontractor.cost.plus(stage_total.subcontractor.cost);
      total.subcontractor.sale = total.subcontractor.sale.plus(stage_total.subcontractor.sale);
      total.subcontractor.profit = total.subcontractor.profit.plus(stage_total.subcontractor.profit);

      total.labor.cost = total.labor.cost.plus(stage_total.labor.cost);
      total.labor.sale = total.labor.sale.plus(stage_total.labor.sale);
      total.labor.profit = total.labor.profit.plus(stage_total.labor.profit);

      total.total_profit = total.total_profit.plus(stage_total.total_profit);
      total.subtotal = total.subtotal.plus(stage_total.subtotal);
      total.forecast = total.forecast.plus(stage_total.forecast);
      total.forecastHours = total.forecastHours.plus(stage_total.forecastHours);
      total.total = total.total.plus(stage_total.total);

      // Store stage totals by stage id for reference
      total.stage[stage.id] = stage_total;
    }
    // Convert all Decimal values back to regular numbers with rounding at the end
    return toRoundedObject(total);
  } catch (e) {
    // Log and return partial totals on error
    console.error(e);
    return total;
  }
};

export const optionCalculation = ({ options = [], isForm = false }) => {
  function calculateTotals(option) {
    const subTasks = Array.isArray(option.subTasks) ? option.subTasks : [];
    const laborCost = subTasks.reduce(
      (sum, subtask) => sum + (parseFloat(subtask.laborTotalCost) || 0),
      0
    );
    const laborProfit = subTasks.reduce(
      (sum, subtask) => sum + (parseFloat(subtask.laborProfit) || 0),
      0
    );

    const materialCost = parseFloat(option.materialTotal_Cost) || 0;
    const materialSale = parseFloat(option.materialTotal_SaleAmount) || 0;
    const materialProfit = parseFloat(option.material_Profit) || 0;

    const subcontractorCost = parseFloat(option.subcontractorTotal_Cost) || 0;
    const subcontractorSale =
      parseFloat(option.subcontractorTotal_SaleAmount) || 0;
    const subcontractorProfit = parseFloat(option.subcontractor_Profit) || 0;

    const laborSale = parseFloat(option.laborTotal_SaleAmount) || 0;

    return {
      material: {
        cost: materialCost,
        sale: materialSale,
        profit: materialProfit,
      },
      subcontractor: {
        cost: subcontractorCost,
        sale: subcontractorSale,
        profit: subcontractorProfit,
      },
      labor: {
        cost: laborCost,
        sale: laborSale,
        profit: laborProfit,
      },
      total_profit: parseFloat(option.totalProfit) || 0,
      subtotal: materialCost + subcontractorCost + laborCost,
      total: materialSale + subcontractorSale + laborSale,
    };
  }

  function formatSubtask(subtask) {
    const materialArr = Array.isArray(subtask.material) ? subtask.material : [];
    const subcontractorArr = Array.isArray(subtask.subcontractor)
      ? subtask.subcontractor
      : [];
    const laborArr = Array.isArray(subtask.labor) ? subtask.labor : [];

    return {
      material: {
        cost: materialArr.reduce(
          (sum, m) => sum + parseFloat(JSON.parse(m).cost || 0),
          0
        ),
        sale: materialArr.reduce(
          (sum, m) => sum + parseFloat(JSON.parse(m).saleAmount || 0),
          0
        ),
        profit: materialArr.reduce((sum, m) => {
          const material = JSON.parse(m);
          return (
            sum +
            (parseFloat(material.saleAmount || 0) -
              parseFloat(material.cost || 0))
          );
        }, 0),
      },
      subcontractor: {
        cost: subcontractorArr.reduce(
          (sum, s) => sum + parseFloat(JSON.parse(s).Cost || 0),
          0
        ),
        sale: subcontractorArr.reduce(
          (sum, s) => sum + parseFloat(JSON.parse(s).saleAmount || 0),
          0
        ),
        profit: subcontractorArr.reduce((sum, s) => {
          const sub = JSON.parse(s);
          return (
            sum +
            (parseFloat(sub.saleAmount || 0) - parseFloat(sub.Cost || 0))
          );
        }, 0),
      },
      labor: {
        cost: laborArr.reduce(
          (sum, l) => sum + parseFloat(JSON.parse(l).cost || 0),
          0
        ),
        sale: laborArr.reduce(
          (sum, l) => sum + parseFloat(JSON.parse(l).saleAmount || 0),
          0
        ),
        profit: parseFloat(subtask.laborProfit) || 0,
      },
      total_profit: parseFloat(subtask.totalProfit) || 0,
      subtotal:
        (parseFloat(subtask.materialTotal_Cost) || 0) +
        (parseFloat(subtask.subcontractorTotal_Cost) || 0) +
        (parseFloat(subtask.laborTotalCost) || 0),
      forecast: parseFloat(subtask.materialForecastCost) || 0,
      total:
        (parseFloat(subtask.materialTotal_SaleAmount) || 0) +
        (parseFloat(subtask.subcontractorTotal_SaleAmount) || 0) +
        (parseFloat(subtask.laborTotal_SaleAmount) || 0),
    };
  }

  let formattedData = {};

  (options || []).forEach((option) => {
    const stagid = option.stageId;
    if (!formattedData[stagid]) {
      formattedData[stagid] = {
        option: {},
        subtask: {},
        material: { cost: 0, sale: 0, profit: 0 },
        subcontractor: { cost: 0, sale: 0, profit: 0 },
        labor: { cost: 0, sale: 0, profit: 0 },
        total_profit: 0,
        forecast_profit: 0,
        revised_cost: 0,
        subtotal: 0,
        total: 0,
      };
    }

    let totals = calculateTotals(option);
    formattedData[stagid].option[option.id] = totals;

    const subTasks = Array.isArray(option.subTasks) ? option.subTasks : [];
    subTasks.forEach((subtask) => {
      formattedData[stagid].subtask[subtask.id] = formatSubtask(subtask);
    });

    formattedData[stagid].material.cost +=
      parseFloat(option.materialTotal_Cost) || 0;
    formattedData[stagid].material.sale +=
      parseFloat(option.materialTotal_SaleAmount) || 0;
    formattedData[stagid].material.profit +=
      parseFloat(option.material_Profit) || 0;

    formattedData[stagid].subcontractor.cost +=
      parseFloat(option.subcontractorTotal_Cost) || 0;
    formattedData[stagid].subcontractor.sale +=
      parseFloat(option.subcontractorTotal_SaleAmount) || 0;
    formattedData[stagid].subcontractor.profit +=
      parseFloat(option.subcontractor_Profit) || 0;

    formattedData[stagid].labor.cost += totals.labor.cost;
    formattedData[stagid].labor.sale += totals.labor.sale;
    formattedData[stagid].labor.profit += totals.labor.profit;

    formattedData[stagid].total_profit += parseFloat(option.totalProfit) || 0;
    formattedData[stagid].subtotal += totals.subtotal;
    formattedData[stagid].total += totals.total;
    formattedData[stagid].revised_cost += totals.total;
  });

  return formattedData;
};

/**
 * Calculate the total sale amount from material, labor, and subcontractor amounts
 * @param {Object} item - Object containing sale amount fields
 * @returns {number} Total sale amount rounded to 2 decimal places
 */
export const findTotal = (item = {}) => {
  const total = toDecimal(item.materialTotal_SaleAmount || 0)
    .plus(toDecimal(item.laborTotal_SaleAmount || 0))
    .plus(toDecimal(item.subcontractorTotal_SaleAmount || 0));
  return roundValue(total, 2);
};

// Calculation for history values (see below for example usage):
//
// totalHistoryCost.materialCost +=
//     parseFloat(idx(task, (_task) => _task.taskHistory[0].materialTotal_SaleAmount)) || 0;
// totalHistoryCost.laborCost +=
//     parseFloat(idx(task, (_task) => _task.taskHistory[0].laborTotal_SaleAmount)) || 0;
// totalHistoryCost.profitMarginCost +=
//     parseFloat(idx(task, (_task) => _task.taskHistory[0].profitMarginCost)) || 0;
// totalHistoryCost.subContractorCost +=
//     parseFloat(idx(task, (_task) => _task.taskHistory[0].subcontractorTotal_SaleAmount)) || 0;
// totalHistoryCost.total =
//     totalHistoryCost.materialCost +
//     totalHistoryCost.laborCost +
//     totalHistoryCost.subContractorCost;
